import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ShareSheet from "@/components/ShareSheet";
import Avatar from "@/components/Avatar";
import InlineText from "@/components/InlineText";
import FollowButton from "@/components/FollowButton";
import BannerStack from "@/components/BannerStack";
import GroupContactListModal from "@/components/GroupContactListModal";
import GoLiveButton from "@/components/GoLiveButton";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, Users, Loader2, X, UserMinus, Share2, Megaphone, Eye, EyeOff, Pencil, Trash2, Save, Crown, BookUser,
} from "lucide-react";

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openInvite, setOpenInvite] = useState(null);
  const [openMembers, setOpenMembers] = useState(null);
  const [openContacts, setOpenContacts] = useState(null);
  const [openBanners, setOpenBanners] = useState(null);
  const [editGroup, setEditGroup] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
    } catch (e) {
      setErr(formatError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (g) => {
    if (!window.confirm(
      `Delete the group "${g.name}"? Every event, banner, expense, bring list, registry item, photo and reminder inside this group will be permanently deleted. Members will be removed.\n\nThis cannot be undone.`
    )) return;
    setDeletingId(g.group_id);
    setErr("");
    try {
      await api.delete(`/groups/${g.group_id}`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>Your groups</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
              The people you share life with.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="bd-btn" onClick={() => setOpenCreate(true)} data-testid="create-group-btn">
              <Plus size={16} /> New group
            </button>
          </div>
        </div>

        {err && <div className="text-sm" style={{ color: "#ff3b30" }} data-testid="groups-error">{err}</div>}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.length === 0 && (
            <div className="bd-surface p-6 sm:col-span-2 lg:col-span-3" data-testid="groups-empty">
              <p style={{ color: "var(--text-dim)" }}>
                No groups yet. Create one and share the invite link with the people you want to include.
              </p>
            </div>
          )}
          {groups.map((g) => {
            const isOwner = user && g.owner_id === user.user_id;
            return (
              <div key={g.group_id} className="bd-card" data-testid={`group-${g.group_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="w-8 h-8 grid place-items-center"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius-btn)" }}
                  >
                    <Users size={14} />
                  </span>
                  <h3 className="font-bold text-lg flex-1 min-w-0 truncate">
                    <InlineText
                      value={g.name}
                      readOnly={!isOwner}
                      testId={`group-name-${g.group_id}`}
                      ariaLabel="Edit group name"
                      onSave={async (next) => {
                        await api.patch(`/groups/${g.group_id}`, { name: next });
                        await load();
                      }}
                    />
                  </h3>
                  {isOwner && <span className="bd-chip" style={{ fontSize: "0.65rem" }}>Originator</span>}
                </div>
                {g.description && <p className="mb-3" style={{ color: "var(--text-dim)" }}>{g.description}</p>}
                <div className="text-xs mb-3" style={{ color: "var(--text-dim)" }}>
                  {g.members.length} member{g.members.length === 1 ? "" : "s"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className="bd-btn"
                    style={{
                      padding: "0.7rem 1.1rem",
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      boxShadow: "0 4px 14px rgba(255, 122, 89, 0.25)",
                    }}
                    onClick={() => setOpenInvite(g.group_id)}
                    data-testid={`invite-btn-${g.group_id}`}
                  >
                    <Share2 size={14} /> Invite to {g.name}
                  </button>
                  <GoLiveButton
                    scope="group"
                    targetId={g.group_id}
                    label="Go Live"
                    variant="primary"
                    testId={`group-go-live-${g.group_id}`}
                    title={`Go live for everyone in ${g.name}`}
                    style={{ padding: "0.7rem 1.1rem", fontSize: "0.95rem" }}
                  />
                  <button
                    className="bd-btn bd-btn-ghost"
                    style={{ padding: "0.45rem 0.75rem", fontSize: "0.8rem" }}
                    onClick={() => setOpenContacts(g.group_id)}
                    data-testid={`contacts-btn-${g.group_id}`}
                    title="Show contact list for everyone in this group"
                  >
                    <BookUser size={12} /> Contact list
                  </button>
                  {isOwner && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.45rem 0.75rem", fontSize: "0.8rem" }}
                      onClick={async () => {
                        // Steve (Feb 2026): opening a group should re-surface every
                        // event the user previously hid from "Coming up" for that
                        // group. Fire-and-forget; failure shouldn't block the modal.
                        try { await api.post(`/groups/${g.group_id}/reset-dismissed`); }
                        catch (e) { console.warn("reset-dismissed for group failed:", e); }
                        setOpenMembers(g.group_id);
                      }}
                      data-testid={`members-btn-${g.group_id}`}
                    >
                      <Users size={12} /> Manage
                    </button>
                  )}
                  {isOwner && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.45rem 0.75rem", fontSize: "0.8rem" }}
                      onClick={() => setOpenBanners(g.group_id)}
                      data-testid={`banners-btn-${g.group_id}`}
                    >
                      <Megaphone size={12} /> Banners
                    </button>
                  )}
                  {isOwner && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.45rem 0.75rem", fontSize: "0.8rem" }}
                      onClick={() => setEditGroup(g)}
                      data-testid={`edit-group-${g.group_id}`}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  {isOwner && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{
                        padding: "0.45rem 0.75rem",
                        fontSize: "0.8rem",
                        color: "#ff3b30",
                      }}
                      onClick={() => handleDelete(g)}
                      disabled={deletingId === g.group_id}
                      data-testid={`delete-group-${g.group_id}`}
                    >
                      {deletingId === g.group_id ? <Loader2 size={12} className="animate-spin" /> : <><Trash2 size={12} /> Delete</>}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <CreateModal open={openCreate} onClose={() => setOpenCreate(false)} onDone={load} />
      {openInvite && (
        <ShareSheet
          groupId={openInvite}
          inviterName={user?.name}
          onClose={() => setOpenInvite(null)}
        />
      )}
      {openMembers && (
        <MembersModal
          groupId={openMembers}
          onClose={() => setOpenMembers(null)}
          onChanged={load}
        />
      )}
      {openContacts && (
        <GroupContactListModal
          groupId={openContacts}
          onClose={() => setOpenContacts(null)}
        />
      )}
      {openBanners && (
        <GroupBannersModal
          groupId={openBanners}
          onClose={() => setOpenBanners(null)}
        />
      )}
      {editGroup && (
        <EditGroupModal
          group={editGroup}
          onClose={() => setEditGroup(null)}
          onSaved={() => { setEditGroup(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function EditGroupModal({ group, onClose, onSaved }) {
  const [name, setName] = useState(group.name || "");
  const [description, setDescription] = useState(group.description || "");
  const [hiddenForMembers, setHiddenForMembers] = useState(!!group.hidden_for_members);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.patch(`/groups/${group.group_id}`, {
        name,
        description,
        hidden_for_members: hiddenForMembers,
      });
      // Trigger calendar refetch in any open Dashboard so the broadcast
      // toggle takes effect immediately.
      window.dispatchEvent(new CustomEvent("bd:visibility-changed"));
      onSaved();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="edit-group-modal"
    >
      <div
        className="bd-surface p-6 w-full max-w-md"
        style={{ background: "var(--bg-elev)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>Edit group</h2>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }} data-testid="edit-group-close">
            <X size={14} />
          </button>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
          Only you (the group originator) can rename or describe this group. Members keep all their personal data; only the group&apos;s display info changes.
        </p>
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="bd-label">Group name</label>
            <input
              className="bd-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="edit-group-name"
            />
          </div>
          <div>
            <label className="bd-label">Description (optional)</label>
            <textarea
              className="bd-input"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="edit-group-description"
            />
          </div>
          <label
            className="flex items-start gap-3 p-3 rounded cursor-pointer"
            style={{ border: "1px solid var(--border)" }}
            data-testid="edit-group-hidden-toggle"
          >
            <input
              type="checkbox"
              checked={hiddenForMembers}
              onChange={(e) => setHiddenForMembers(e.target.checked)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-sm">Hide this group from members&apos; calendars</div>
              <div className="text-xs" style={{ color: "var(--text-dim)" }}>
                Members can still see the group on the Groups page, but its events stop appearing on their calendar grid. You (the originator) still see everything. Useful for &quot;paused&quot; groups or surprise-planning channels.
              </div>
            </div>
          </label>
          {err && <div className="text-sm" style={{ color: "#ff3b30" }} data-testid="edit-group-error">{err}</div>}
          <button className="bd-btn w-full" disabled={busy} data-testid="edit-group-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save changes</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function GroupBannersModal({ groupId, onClose }) {
  const [group, setGroup] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
    } catch (err) {
      console.error("Failed to load group banners:", err);
    }
  };
  useEffect(() => { load(); }, [groupId]);

  const toggleGlobal = async () => {
    setBusy(true);
    try {
      const next = !group?.disable_global_banners;
      await api.patch(`/groups/${groupId}/banner-settings`, { disable_global_banners: next });
      await load();
    } finally { setBusy(false); }
  };

  if (!group) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div className="bd-surface p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="group-banners-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>Banners for {group.name}</h2>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }}><X size={14} /></button>
        </div>
        <div className="bd-card mb-5 flex items-center justify-between gap-3" style={{ padding: "0.75rem 1rem" }}>
          <div>
            <div className="font-semibold">Global app-wide banners</div>
            <p className="text-xs" style={{ color: "var(--text-dim)" }}>
              Turn off if you don&apos;t want platform-wide banners showing inside this group.
            </p>
          </div>
          <button
            className="bd-btn bd-btn-ghost"
            onClick={toggleGlobal}
            disabled={busy}
            data-testid="group-toggle-global-banners"
          >
            {group.disable_global_banners
              ? <><EyeOff size={12} /> Disabled</>
              : <><Eye size={12} /> Showing</>}
          </button>
        </div>
        <h3 className="font-semibold mb-2 flex items-center gap-2"><Megaphone size={14} /> Your group&apos;s banners</h3>
        <BannerStack
          groupId={groupId}
          scopeForAdd={`group:${groupId}`}
          canManage
        />
      </div>
    </div>
  );
}

function CreateModal({ open, onClose, onDone }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [schedulingMode, setSchedulingMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/groups", { name, description: desc, scheduling_mode: schedulingMode });
      onDone && onDone();
      onClose();
      setName(""); setDesc(""); setSchedulingMode(false);
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bd-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="create-group-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">New group</h2>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }}><X size={14} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="bd-label">Group name</label>
            <input className="bd-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="The Smith Family" data-testid="group-name-input" />
          </div>
          <div>
            <label className="bd-label">Description (optional)</label>
            <input className="bd-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Aunts, uncles, cousins" data-testid="group-desc-input" />
          </div>
          <div className="bd-card" style={{ padding: "0.85rem 1rem" }} data-testid="scheduling-mode-row">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={schedulingMode}
                onChange={(e) => setSchedulingMode(e.target.checked)}
                data-testid="scheduling-mode-toggle"
              />
              <div>
                <div className="font-semibold text-sm">Scheduling mode</div>
                <p className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>
                  Turns this group&apos;s calendar into a dense, color-coded planning view — perfect for fitness classes, recurring slots, and busy weekly routines.
                </p>
              </div>
            </label>
          </div>
          {err && <div className="text-sm" style={{ color: "#ff3b30" }}>{err}</div>}
          <button className="bd-btn w-full" disabled={busy} data-testid="group-create-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Create group"}
          </button>
        </form>
      </div>
    </div>
  );
}

function MembersModal({ groupId, onClose, onChanged }) {
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/groups/${groupId}`);
    setGroup(data);
  };
  useEffect(() => { load(); }, [groupId]);

  const remove = async (uid) => {
    setBusyId(uid);
    try {
      await api.post(`/groups/${groupId}/remove-member`, { user_id: uid });
      await load();
      onChanged && onChanged();
    } catch (e) {
      window.alert(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  const setRole = async (uid, role, name) => {
    const verb = role === "firsthand" ? "promote" : "demote";
    if (!window.confirm(
      `${verb === "promote" ? "Make" : "Remove"} ${name || "this member"} ${verb === "promote" ? "an Admin?" : "as Admin?"}\n\n` +
      (role === "firsthand"
        ? "Admins can invite + remove members and edit anyone's events. They cannot rename, delete, or transfer the group."
        : "They'll go back to being a regular Member.")
    )) return;
    setBusyId(uid);
    try {
      const { data } = await api.post(`/groups/${groupId}/role`, { user_id: uid, role });
      setGroup({ ...group, ...data, members_info: group.members_info, my_role: group.my_role });
      await load();
      onChanged && onChanged();
    } catch (e) {
      window.alert(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  const makeOwner = async (uid, name) => {
    const ok = window.confirm(
      `Make ${name || "this member"} the new originator of "${group.name}"?\n\n` +
      "They'll be able to edit the group, manage members, and delete it. " +
      "You will remain a regular member."
    );
    if (!ok) return;
    setBusyId(uid);
    try {
      const { data } = await api.post(`/groups/${groupId}/transfer-owner`, { new_owner_id: uid });
      setGroup(data);
      onChanged && onChanged();
    } catch (e) {
      window.alert(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!group) return null;
  const myRole = group.my_role; // "owner" | "firsthand" | "member"
  const isOwner = myRole === "owner";
  const canManage = myRole === "owner" || myRole === "firsthand";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-2 sm:p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div
        className="bd-surface w-full max-w-lg flex flex-col"
        style={{ maxHeight: "92vh", padding: 0 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="members-modal"
      >
        {/* Header pinned at top — wraps cleanly on iPhone width */}
        <div
          className="flex flex-wrap items-center gap-2 px-4 sm:px-5 pt-4 pb-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 className="text-lg sm:text-xl font-bold flex-1 min-w-0 truncate">Manage members</h2>
          <FollowButton
            targetType="group"
            targetId={groupId}
            label="Follow"
            testId={`group-follow-${groupId}`}
          />
          <button
            onClick={onClose}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.35rem" }}
            data-testid="members-modal-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        {/* Scrollable body so long member lists don't blow out the modal */}
        <ul className="space-y-2 overflow-y-auto px-4 sm:px-5 py-3 flex-1 min-h-0">
          {(group.members_info || []).map((m) => {
            const role = m.role || (m.user_id === group.owner_id ? "owner" : (group.firsthands || []).includes(m.user_id) ? "firsthand" : "member");
            const isMe = user && m.user_id === user.user_id;
            return (
              <li
                key={m.user_id}
                className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded"
                style={{ border: "1px solid var(--border)" }}
                data-testid={`member-${m.user_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Avatar name={m.name || m.email} picture={m.picture} size={32} testId={`member-avatar-${m.user_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate flex items-center gap-1.5">
                      {m.name}
                      {role === "owner" && (
                        <span className="bd-chip flex items-center gap-1" style={{ fontSize: "0.6rem", padding: "0.1rem 0.4rem" }}>
                          <Crown size={9} /> Originator
                        </span>
                      )}
                      {role === "firsthand" && (
                        <span className="bd-chip flex items-center gap-1" style={{ fontSize: "0.6rem", padding: "0.1rem 0.4rem", background: "var(--secondary)", color: "var(--secondary-fg)", borderColor: "var(--secondary)" }}>
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--text-dim)" }}>{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end sm:flex-nowrap">
                  {!isMe && (
                    <FollowButton
                      targetType="member"
                      targetId={m.user_id}
                      label="Follow"
                      testId={`member-follow-${m.user_id}`}
                    />
                  )}
                  {/* Owner-only: role + ownership changes */}
                  {isOwner && role !== "owner" && (
                    <>
                      {role === "member" && (
                        <button
                          className="bd-btn bd-btn-ghost"
                          style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                          onClick={() => setRole(m.user_id, "firsthand", m.name)}
                          disabled={busyId === m.user_id}
                          data-testid={`member-promote-${m.user_id}`}
                          title="Make this member an Admin (can invite + remove members and edit any event)"
                        >
                          {busyId === m.user_id ? <Loader2 size={12} className="animate-spin" /> : "Make Admin"}
                        </button>
                      )}
                      {role === "firsthand" && (
                        <button
                          className="bd-btn bd-btn-ghost"
                          style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                          onClick={() => setRole(m.user_id, "member", m.name)}
                          disabled={busyId === m.user_id}
                          data-testid={`member-demote-${m.user_id}`}
                          title="Remove Admin role (back to regular Member)"
                        >
                          {busyId === m.user_id ? <Loader2 size={12} className="animate-spin" /> : "Remove Admin"}
                        </button>
                      )}
                      <button
                        className="bd-btn bd-btn-ghost"
                        style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                        onClick={() => makeOwner(m.user_id, m.name)}
                        disabled={busyId === m.user_id}
                        data-testid={`member-make-owner-${m.user_id}`}
                        title="Hand originator responsibilities to this member"
                      >
                        <Crown size={12} /> Make originator
                      </button>
                    </>
                  )}
                  {/* Owner or Firsthand can remove; Firsthand can't remove a Firsthand */}
                  {canManage && !isMe && role !== "owner" && (myRole === "owner" || role !== "firsthand") && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", color: "#ff3b30" }}
                      onClick={() => remove(m.user_id)}
                      disabled={busyId === m.user_id}
                      data-testid={`member-remove-${m.user_id}`}
                    >
                      <UserMinus size={12} /> Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {isOwner && (
          <div
            className="px-4 sm:px-5 py-3 flex items-center justify-between gap-3"
            style={{ borderTop: "1px solid var(--border)" }}
            data-testid="members-modal-danger"
          >
            <div>
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
                Danger zone
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                Deletes this group and every event, expense, bring list and registry item inside it. Cannot be undone.
              </p>
            </div>
            <button
              className="bd-btn bd-btn-ghost"
              style={{ color: "#ff3b30", borderColor: "color-mix(in srgb, #ff3b30 30%, transparent)" }}
              onClick={async () => {
                if (!window.confirm(
                  `Delete the group "${group.name}"? Every event, expense, bring list, and registry item inside this group will be permanently deleted.\n\nThis cannot be undone.`
                )) return;
                try {
                  await api.delete(`/groups/${groupId}`);
                  onChanged && onChanged();
                  onClose();
                } catch (e) {
                  window.alert(formatError(e));
                }
              }}
              data-testid="members-modal-delete-group"
            >
              <Trash2 size={12} /> Delete this group
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
